<template>
    <div>
        <div
            tabindex="0"
            @focus="loop_to_last"
        ></div>
        <div class="modal-header">
            <dismiss-link
                v-if="dismissable"
                id="first_focusable"
                @click="dismiss"
                class="btn btn-clear float-right"
            />
            <div class="modal-title h5">
                <span v-if="fetching">{{ $root.translate("Fetching your invitation...") }}</span>
                <span v-else>{{ $root.translate("Signup") }}</span>
            </div>
        </div>
        <div class="modal-body">
            <div
                v-if="fetching"
                class="loader-container"
            >
                <loader />
            </div>
            <base-form
                v-else
                :has-required-fields="true"
                @submit.prevent.stop="signup"
            >
                <form-input
                    :id="dismissable ? 'email' : 'first_focusable'"
                    ref="first_field"
                    v-model="email"
                    name="email"
                    type="email"
                    :error="has_error('email')"
                    :required="true"
                    :maybe-autofilled="true"
                    @input="update_email"
                    @blur="update_email"
                >
                    <template v-slot:label v-if="!labelLess">{{ $root.translate("Email") }}</template>
                </form-input>
                <password-editor
                    id="password"
                    v-model="password"
                    type="password"
                    name="password"
                    :error="has_error('password')"
                    :required="true"
                >
                    <template v-slot:label v-if="!labelLess">{{ $root.translate("Password") }}</template>
                </password-editor>

                <div class="columns">
                    <div class="column col-6 col-sm-12 mb-2">
                        <form-input
                            id="first_name"
                            v-model="first_name"
                            name="first_name"
                            :error="has_error('first_name')"
                            :required="false"
                        >
                            <template v-slot:label v-if="!labelLess">{{ $root.translate("First Name") }}</template>
                        </form-input>
                    </div>
                    <div class="column col-6 col-sm-12 mb-2">
                        <form-input
                            id="last_name"
                            v-model="last_name"
                            name="last_name"
                            :error="has_error('last_name')"
                            :required="false"
                        >
                            <template v-slot:label v-if="!labelLess">{{ $root.translate("Last Name") }}</template>
                        </form-input>
                    </div>
                </div>

                <div class="text-center mt-8">
                    <feedback-button
                        type="submit"
                        :status="feedback_status"
                        :waiting="loading"
                        :single-use="false"
                        color="primary"
                    >
                        <template v-slot:message>
                            <div
                                :class="{ 'text-success': (feedback_status === true), 'text-error': (feedback_status === false) }"
                            >
                                <div v-if="!!already_invited" class="toast toast-error text-center">
                                    {{ already_invited }}
                                    <br />
                                    <small><a href @click.prevent="resend_invitation">({{ $root.translate("Please resend me my invitation link...") }})</a></small>
                                </div>
                                <span v-else>{{ feedback_message }}</span>
                            </div>
                        </template>
                        {{ $root.translate("Sign Up!") }}
                    </feedback-button>
                </div>
            </base-form>
        </div>
        <div
            v-if="!invitation_token"
            class="modal-footer"
        >
            <default-button
                id="last_focusable"
                flavor="link"
                size="sm"
                @click.prevent="goto('login')"
            >
                {{ $root.translate("I'm already signed-up - let me login") }}
            </default-button>
        </div>
        <div
            tabindex="0"
            @focus="loop_to_first"
        ></div>
    </div>
</template>

<script>
import is_panel from "@/nibnut/dialogs/Authentication/IsPanel"

import Loader from "@/custom/components/Loader"
import { DismissLink, BaseForm, FormInput, PasswordEditor, FeedbackButton, DefaultButton } from "@/nibnut/components"

export default {
    mixins: [is_panel],
    components: {
        DismissLink,
        BaseForm,
        FormInput,
        PasswordEditor,
        FeedbackButton,
        DefaultButton,
        Loader
    },
    mounted () {
        this.load_invitation()
    },
    methods: {
        load_invitation () {
            if(window.location.search) {
                const matches = window.location.search.match(/(?:\?|&)invitation=([^&]+)(?:&|$)/)
                if(matches) {
                    const token = matches[1]

                    this.fetching = true
                    this.$store.dispatch("LOAD_INVITATION", {
                        token
                    }).then(invitation_data => {
                        this.invitation_token = token
                        this.first_name = invitation_data.first_name
                        this.last_name = invitation_data.last_name
                        this.email = invitation_data.email
                    }).catch(this.receive_error).then(() => {
                        this.fetching = false
                    })
                }
            }
        },
        resend_invitation () {
            if(this.email) {
                /*
                this.$store.dispatch("RECORD_ACTION", { entity: "user", action: "re-invite", data: { email: this.email }, passthru: true }).then(() => {
                    this.$success("<strong>" + this.$root.translate("Invitation link re-sent!") + "</strong><br>" + this.$root.translate("Check your inbox... (or junk folder, just in case)"))
                }).catch(error => {
                    this.$error("<strong>" + error.header + "</strong><br>" + error.message)
                })
                */
            }
        },
        signup () {
            setTimeout(() => {
                const signup = () => {
                    this.loading = true
                    this.clear_all_errors()
                    this.$store.dispatch("SIGNUP", {
                        data: {
                            first_name: this.first_name,
                            last_name: this.last_name,
                            email: this.email,
                            password: encodeURIComponent(this.password),
                            invitation_token: this.invitation_token
                        }
                    }).then(() => {
                        this.$emit("input", "")
                        this.first_name = ""
                        this.last_name = ""
                        this.password = ""
                        this.invitation_token = ""
                        this.$store.dispatch("UNREQUEST_LOGIN")
                        window.location.search = ""
                    }).catch(error => {
                        if(error) {
                            if(error.topic_id && (error.topic_id === "invitation_token")) {
                                this.already_invited = error.message
                            } else this.receive_error(error)
                        }
                    }).then(() => {
                        this.loading = false
                    })
                }
                if(this.$refs.recaptcha) {
                    this.verification_callback = () => {
                        if(this.recaptcha) signup()
                        this.verification_callback = null
                    }
                    this.$refs.recaptcha.execute()
                } else signup()
            }, this.autofill_timeout) // need a timeout slightly longer than input field's debounce. Otherwise, Enter key happens before this.field is updated!
        }
    },
    data () {
        return {
            fetching: false,
            invitation_token: "",
            first_name: "",
            last_name: "",
            password: ""
        }
    }
}
</script>

<style lang="scss" scoped>

</style>
